import { useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Loader, type BadgeVariant } from '@lms/ui';
import { AuthenticatedImage } from '@/components/AuthenticatedImage';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { ExpedienteEmptyState } from '@/features/academic/ExpedienteEmptyState';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/lib/api';

interface ExpedienteRecord {
  coursesEnrolled?: number;
  coursesPassed?: number;
  coursesInProgress?: number;
  averageGrade?: number | null;
  user?: {
    enrollments?: {
      status?: string;
      course?: {
        id: string;
        name: string;
        coverImageUrl?: string | null;
        bannerImageUrl?: string | null;
        trashed?: boolean;
      } | null;
    }[];
    studentGrades?: {
      score?: number | null;
      status?: string;
      gradedAt?: string | null;
      gradeItem?: {
        title: string;
        maxScore?: number;
        courseId?: string | null;
        courseName?: string | null;
      };
    }[];
  };
}

function courseStatusBadge(status?: string): { label: string; className: string } {
  switch (status) {
    case 'passed':
    case 'completed':
      return { label: 'APROBADO', className: 'bg-success-600 text-white' };
    case 'failed':
      return { label: 'REPROBADO', className: 'bg-error-600 text-white' };
    case 'in_progress':
    case 'active':
      return { label: 'ACTIVO', className: 'bg-dash-secondary text-white' };
    case 'exempt':
      return { label: 'EXIMIDO', className: 'bg-warning-500 text-white' };
    case 'voided':
      return { label: 'ANULADO', className: 'bg-surface-500 text-white' };
    case 'pending':
    default:
      return { label: 'SIN INICIAR', className: 'bg-secondary-600 text-white' };
  }
}

function gradeStatusLabel(status?: string): string {
  switch (status) {
    case 'pending':
      return 'Pendiente';
    case 'in_progress':
      return 'En proceso';
    case 'passed':
      return 'Aprobado';
    case 'failed':
      return 'Reprobado';
    case 'exempt':
      return 'Eximido';
    case 'voided':
      return 'Anulado';
    default:
      return status ?? '—';
  }
}

function gradeBadgeVariant(status?: string): BadgeVariant {
  switch (status) {
    case 'passed':
      return 'success';
    case 'failed':
      return 'danger';
    case 'in_progress':
      return 'secondary';
    case 'exempt':
      return 'warning';
    default:
      return 'default';
  }
}

function enrollmentStatusLabel(status?: string): string {
  switch (status) {
    case 'completed':
      return 'Completado';
    case 'active':
      return 'Activo';
    case 'pending':
      return 'Pendiente';
    case 'failed':
      return 'Reprobado';
    default:
      return status ?? '';
  }
}

export function MyExpedientePage() {
  const { user, isLoading: authLoading } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['my-expediente', user?.id],
    queryFn: () => api<ExpedienteRecord>(`/academic-records/${user!.id}`),
    enabled: Boolean(user?.id),
  });

  const courseCards = useMemo(() => {
    const enrollments = data?.user?.enrollments ?? [];
    const grades = data?.user?.studentGrades ?? [];
    const gradesByCourse = new Map<string, typeof grades>();

    for (const grade of grades) {
      const courseId = grade.gradeItem?.courseId;
      if (!courseId) continue;
      gradesByCourse.set(courseId, [...(gradesByCourse.get(courseId) ?? []), grade]);
    }

    return enrollments
      .filter((e) => e.course)
      .map((enrollment) => {
        const course = enrollment.course!;
        const courseGrades = gradesByCourse.get(course.id) ?? [];
        const scored = courseGrades.filter((g) => g.score != null && (g.gradeItem?.maxScore ?? 0) > 0);
        const percentages = scored.map(
          (g) => ((g.score ?? 0) / (g.gradeItem?.maxScore ?? 100)) * 100,
        );
        const finalPercentage =
          percentages.length > 0
            ? percentages.reduce((a, b) => a + b, 0) / percentages.length
            : null;
        const pct = finalPercentage ?? 0;
        const dominantStatus =
          courseGrades.find((g) => g.status && g.status !== 'pending')?.status ?? enrollment.status;
        const badge = courseStatusBadge(dominantStatus);

        return {
          courseId: course.id,
          courseName: course.name,
          image: course.coverImageUrl ?? course.bannerImageUrl,
          badge,
          itemsGraded: scored.length,
          itemsTotal: courseGrades.length,
          finalPercentage,
          pct,
          statusLabel: enrollmentStatusLabel(enrollment.status),
          courseUrl: !course.trashed ? `/mis-cursos/${course.id}` : null,
        };
      });
  }, [data]);

  const recentGrades = useMemo(() => {
    return [...(data?.user?.studentGrades ?? [])]
      .sort((a, b) => {
        const da = a.gradedAt ? new Date(a.gradedAt).getTime() : 0;
        const db = b.gradedAt ? new Date(b.gradedAt).getTime() : 0;
        return db - da;
      })
      .slice(0, 15);
  }, [data]);

  if (authLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const average = data?.averageGrade ?? null;
  const averageBar = average != null ? Math.min(100, Math.max(0, average)) : 0;
  const averageIsLow = average != null && average < 60;
  const coursesPassed = data?.coursesPassed ?? 0;

  return (
    <div className="lms-expediente lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <HubBackLink to="/administracion?tab=general" />
          <div>
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Mi Expediente Académico</h1>
            <p className="mt-1 text-dash-on-surface-variant">
              Consulta tu progreso, estadísticas y calificaciones detalladas.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <p className="mb-6 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <>
          <section className="mb-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" aria-label="Resumen académico">
            <div className="lms-expediente-stat rounded-xl border border-dash-outline-variant bg-white p-6">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm font-medium text-dash-on-surface-variant">Cursos inscritos</span>
                <span className="material-symbols-outlined text-dash-primary" aria-hidden="true">
                  school
                </span>
              </div>
              <p className="text-2xl font-bold text-dash-on-surface">{data?.coursesEnrolled ?? 0}</p>
              <p className="mt-2 text-xs font-semibold text-dash-primary">Total matriculado</p>
            </div>

            <div className="lms-expediente-stat rounded-xl border border-dash-outline-variant bg-white p-6">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm font-medium text-dash-on-surface-variant">Aprobados</span>
                <span className="material-symbols-outlined text-success-600" aria-hidden="true">
                  check_circle
                </span>
              </div>
              <p className="text-2xl font-bold text-dash-on-surface">{coursesPassed}</p>
              <p className="mt-2 text-xs text-dash-on-surface-variant">
                {coursesPassed < 1
                  ? 'Sin cursos completados'
                  : coursesPassed === 1
                    ? 'Curso completado'
                    : 'Cursos completados'}
              </p>
            </div>

            <div className="lms-expediente-stat rounded-xl border border-dash-outline-variant bg-white p-6">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm font-medium text-dash-on-surface-variant">En progreso</span>
                <span className="material-symbols-outlined text-dash-secondary" aria-hidden="true">
                  pending
                </span>
              </div>
              <p className="text-2xl font-bold text-dash-on-surface">{data?.coursesInProgress ?? 0}</p>
              <p className="mt-2 text-xs text-dash-on-surface-variant">Activos actualmente</p>
            </div>

            <div className="lms-expediente-stat relative overflow-hidden rounded-xl border border-dash-outline-variant bg-white p-6">
              {averageIsLow && (
                <span className="absolute right-0 top-0 rounded-bl-lg bg-dash-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                  Crítico
                </span>
              )}
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm font-medium text-dash-on-surface-variant">Promedio general</span>
                <span
                  className={`material-symbols-outlined ${averageIsLow ? 'text-error-600' : 'text-dash-primary'}`}
                  aria-hidden="true"
                >
                  analytics
                </span>
              </div>
              <p className={`text-2xl font-bold ${averageIsLow ? 'text-error-600' : 'text-dash-on-surface'}`}>
                {average != null ? `${average.toFixed(1)}%` : '—'}
              </p>
              {average != null && (
                <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-dash-outline-variant">
                  <div
                    className={`${averageIsLow ? 'bg-error-600' : 'bg-dash-secondary'} h-full rounded-full transition-[width]`}
                    style={{ width: `${averageBar}%` }}
                  />
                </div>
              )}
            </div>
          </section>

          <section className="mb-10" aria-labelledby="expediente-courses-heading">
            <div className="mb-6 flex items-center gap-2">
              <span className="material-symbols-outlined text-dash-primary" aria-hidden="true">
                auto_stories
              </span>
              <h2 id="expediente-courses-heading" className="text-2xl font-semibold text-dash-on-surface">
                Mis cursos
              </h2>
            </div>

            {courseCards.length === 0 ? (
              <div className="rounded-xl border border-dash-outline-variant bg-white p-8">
                <ExpedienteEmptyState
                  title="Sin calificaciones de curso"
                  description="Cuando completes actividades calificables, aparecerán aquí."
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {courseCards.map((course) => (
                  <article
                    key={course.courseId}
                    className="group overflow-hidden rounded-xl border border-dash-outline-variant bg-white transition-shadow hover:shadow-lg"
                  >
                    <div className="relative h-32 overflow-hidden bg-dash-surface-container">
                      {course.image && (
                        <AuthenticatedImage
                          src={course.image}
                          alt=""
                          className="h-full w-full object-cover opacity-60 transition-transform duration-500 group-hover:scale-105"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-white to-transparent" />
                      <div className="absolute bottom-4 left-6">
                        <span className={`${course.badge.className} rounded-full px-3 py-1 text-xs font-bold`}>
                          {course.badge.label}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="mb-4 text-xl font-semibold text-dash-primary">{course.courseName}</h3>
                      <div className="mb-2 flex items-center justify-between text-sm">
                        <span className="text-dash-on-surface-variant">Actividades calificadas</span>
                        <span className="font-bold text-dash-on-surface">
                          {course.itemsGraded}/{course.itemsTotal}
                        </span>
                      </div>
                      <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-dash-outline-variant">
                        <div className="h-full bg-dash-secondary" style={{ width: `${Math.min(100, Math.max(0, course.pct))}%` }} />
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span
                          className={`text-xs font-bold ${course.pct > 0 ? 'text-dash-secondary' : 'text-dash-on-surface-variant'}`}
                        >
                          {course.finalPercentage != null ? `${course.finalPercentage.toFixed(1)}%` : '—'}{' '}
                          {course.statusLabel}
                        </span>
                        {course.courseUrl && (
                          <Link
                            to={course.courseUrl}
                            className="inline-flex items-center gap-1 text-sm font-medium text-dash-primary hover:underline"
                          >
                            Ver curso
                            <span className="material-symbols-outlined text-base" aria-hidden="true">
                              open_in_new
                            </span>
                          </Link>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>

          <section
            className="overflow-hidden rounded-xl border border-dash-outline-variant bg-white"
            aria-labelledby="expediente-grades-heading"
          >
            <div className="flex items-center justify-between border-b border-dash-outline-variant p-6">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-dash-primary" aria-hidden="true">
                  analytics
                </span>
                <h2 id="expediente-grades-heading" className="text-2xl font-semibold text-dash-on-surface">
                  Últimas calificaciones
                </h2>
              </div>
            </div>

            {recentGrades.length === 0 ? (
              <div className="p-8">
                <ExpedienteEmptyState title="Sin notas registradas" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left">
                  <thead className="bg-secondary-50">
                    <tr>
                      <th className="px-6 py-4 text-sm font-medium text-dash-on-surface-variant">Curso</th>
                      <th className="px-6 py-4 text-sm font-medium text-dash-on-surface-variant">Actividad</th>
                      <th className="px-6 py-4 text-sm font-medium text-dash-on-surface-variant">Nota</th>
                      <th className="px-6 py-4 text-sm font-medium text-dash-on-surface-variant">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-dash-outline-variant">
                    {recentGrades.map((grade, index) => {
                      const max = grade.gradeItem?.maxScore ?? 0;
                      const score = grade.score;
                      const pct =
                        score != null && max > 0 ? ((score / max) * 100).toFixed(1) : null;
                      const rowFailed = grade.status === 'failed';

                      return (
                        <tr key={index} className="transition-colors hover:bg-secondary-50">
                          <td className="px-6 py-5">
                            <span className="text-sm font-bold text-dash-primary">
                              {grade.gradeItem?.courseName ?? '—'}
                            </span>
                          </td>
                          <td className="px-6 py-5 text-dash-on-surface">{grade.gradeItem?.title ?? '—'}</td>
                          <td className="px-6 py-5">
                            <div className="flex flex-col">
                              {score != null && max > 0 ? (
                                <span className="font-bold text-dash-on-surface">
                                  {score.toFixed(1)}
                                  <span className="font-normal text-dash-on-surface-variant">
                                    {' '}
                                    / {Number.isInteger(max) ? max : max.toFixed(1)}
                                  </span>
                                </span>
                              ) : (
                                <span className="font-bold text-dash-on-surface">—</span>
                              )}
                              {pct != null && (
                                <span
                                  className={`text-xs font-bold ${rowFailed ? 'text-error-600' : 'text-dash-on-surface-variant'}`}
                                >
                                  {pct}%
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            {grade.status ? (
                              <Badge variant={gradeBadgeVariant(grade.status)}>
                                {gradeStatusLabel(grade.status)}
                              </Badge>
                            ) : (
                              <span className="text-sm text-dash-on-surface-variant">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
